import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import AirDatePicker from '../ui/AirDatePicker';
import fetchApi from '../../utils/api';
import Swal from 'sweetalert2';

const EMPTY_ROW = () => ({
    partyType: '',
    partyId: '',
    partyName: '',
    date: new Date().toISOString().split('T')[0],
    expenseName: '',
    invoiceNo: '',
    expensePurpose: '',
    netAmount: 0,
    gstAmount: 0,
    rowTotal: 0
});

function AddExpensesModal({ isOpen, onClose, isPage }) {
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [allParties, setAllParties] = useState([]);
    const [selectedTypeId, setSelectedTypeId] = useState('');
    const [selectedType, setSelectedType] = useState(null);
    const [rows, setRows] = useState([EMPTY_ROW()]);
    const [partySearch, setPartySearch] = useState({});
    const [partyDropdown, setPartyDropdown] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [partyActiveIndex, setPartyActiveIndex] = useState(-1);
    const dropdownRef = useRef({});
    const rowRefs = useRef([]);

    const handleKeyDown = (e, idx, field) => {
        if (field === 'partyName' && partyDropdown[idx]) {
            const filtered = getFilteredParties(partySearch[idx] !== undefined ? partySearch[idx] : rows[idx].partyName);
            if (filtered.length > 0) {
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setPartyActiveIndex(prev => (prev + 1) % filtered.length);
                    return;
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setPartyActiveIndex(prev => (prev - 1 + filtered.length) % filtered.length);
                    return;
                } else if (e.key === 'Enter') {
                    if (partyActiveIndex >= 0 && partyActiveIndex < filtered.length) {
                        e.preventDefault();
                        const chosen = filtered[partyActiveIndex];
                        selectParty(idx, chosen);
                        setPartyActiveIndex(-1);
                        setTimeout(() => {
                            rowRefs.current[idx]?.date?.focus();
                        }, 50);
                        return;
                    }
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setPartyDropdown(prev => ({ ...prev, [idx]: false }));
                    setPartyActiveIndex(-1);
                    return;
                }
            }
        }

        if (e.key === 'Enter') {
            e.preventDefault();
            if (field === 'partyName') {
                setPartyDropdown(prev => ({ ...prev, [idx]: false }));
                setPartyActiveIndex(-1);
            }
            const fields = ['partyName', 'date', 'expenseName', 'invoiceNo', 'expensePurpose', 'netAmount'];
            const currentFieldIndex = fields.indexOf(field);
            if (currentFieldIndex < fields.length - 1) {
                const nextField = fields[currentFieldIndex + 1];
                rowRefs.current[idx]?.[nextField]?.focus();
            } else {
                if (idx === rows.length - 1) {
                    addRow();
                    setTimeout(() => {
                        rowRefs.current[idx + 1]?.partyName?.focus();
                    }, 100);
                } else {
                    rowRefs.current[idx + 1]?.partyName?.focus();
                }
            }
        }
    };

    // Load expense types and parties
    useEffect(() => {
        const load = async () => {
            // Load Expense Types
            try {
                const etRes = await fetchApi('/expense-type');
                const types = etRes.expenseTypes || [];
                setExpenseTypes(types);
                if (types.length > 0) {
                    setSelectedTypeId(String(types[0].id));
                    setSelectedType(types[0]);
                }
            } catch (err) {
                console.error("Load expense types error:", err);
            }

            // Load Unified Accounts/Parties
            try {
                const accountRes = await fetchApi('/account/unified?limit=1000');
                const accountParties = (accountRes.accounts || [])
                    .filter(a => {
                        const name = (a.partyName || a.account_name || '').toLowerCase();
                        return !name.includes('cash') && !name.includes('bank');
                    })
                    .map(a => ({
                        id: a.id,
                        name: a.partyName,
                        type: a.type || 'Account',
                        balance: parseFloat(a.balance) || 0
                    }));
                console.log("AddExpensesModal - Loaded Unified Parties:", accountParties);
                setAllParties(accountParties);
            } catch (err) {
                console.error("Load unified parties error:", err);
            }
        };
        if (isOpen || isPage) load();
    }, [isOpen, isPage]);

    // Handle scroll of highlighted option in dropdown
    useEffect(() => {
        const openIdx = Object.keys(partyDropdown).find(k => partyDropdown[k] === true);
        if (openIdx !== undefined && partyActiveIndex >= 0 && dropdownRef.current[openIdx]) {
            const container = dropdownRef.current[openIdx];
            const activeEl = container.children[partyActiveIndex];
            if (activeEl) {
                activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [partyActiveIndex, partyDropdown]);

    const handleTypeChange = (e) => {
        const id = e.target.value;
        setSelectedTypeId(id);
        const t = expenseTypes.find(x => String(x.id) === id);
        setSelectedType(t || null);
        // Recalculate GST for all rows
        if (t) {
            setRows(prev => prev.map(r => recalcRow(r, t)));
        }
    };

    const recalcRow = (row, type) => {
        const net = parseFloat(row.netAmount) || 0;
        const taxRate = ((parseFloat(type?.tax1) || 0) + (parseFloat(type?.tax2) || 0));
        const gst = parseFloat(((net * taxRate) / 100).toFixed(2));
        return { ...row, gstAmount: gst, rowTotal: parseFloat((net + gst).toFixed(2)) };
    };

    const updateRow = (index, field, value) => {
        setRows(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            if (field === 'netAmount') {
                updated[index] = recalcRow(updated[index], selectedType);
            }
            return updated;
        });
    };

    const addRow = () => setRows(prev => [...prev, EMPTY_ROW()]);
    const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

    // Party search filter
    const getFilteredParties = (search) => {
        if (!search || search.trim().length === 0) {
            return allParties.slice(0, 15);
        }
        const q = search.toLowerCase();
        return allParties.filter(p => p.name && p.name.toLowerCase().includes(q)).slice(0, 15);
    };

    const handlePartySearch = (index, value) => {
        setPartySearch(prev => ({ ...prev, [index]: value }));
        setPartyDropdown(prev => ({ ...prev, [index]: true }));
        setPartyActiveIndex(-1);
        updateRow(index, 'partyName', value);
        updateRow(index, 'partyId', '');
        updateRow(index, 'partyType', '');
    };

    const selectParty = (index, party) => {
        updateRow(index, 'partyId', party.id);
        updateRow(index, 'partyName', party.name);
        updateRow(index, 'partyType', party.type);
        setPartySearch(prev => ({ ...prev, [index]: party.name }));
        setPartyDropdown(prev => ({ ...prev, [index]: false }));
        setPartyActiveIndex(-1);
    };

    const totals = rows.reduce((acc, r) => ({
        net: acc.net + (parseFloat(r.netAmount) || 0),
        gst: acc.gst + (parseFloat(r.gstAmount) || 0),
        total: acc.total + (parseFloat(r.rowTotal) || 0)
    }), { net: 0, gst: 0, total: 0 });

    const handleSubmit = async () => {

    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        if (!selectedTypeId) { toast.error("Please select an Expense Type"); return; }
        const validRows = rows.filter(r => r.expenseName || r.partyName || r.netAmount > 0);
        if (validRows.length === 0) { toast.error("Please fill at least one expense row"); return; }

        setSubmitting(true);
        try {
            const t = selectedType;
            await fetchApi('/expense', {
                method: 'POST',
                body: JSON.stringify({
                    expenseTypeId: selectedTypeId,
                    expenseTypeName: t?.expenseTypeName || t?.expense_type_name || '',
                    tax1: t?.tax1 || 0,
                    tax2: t?.tax2 || 0,
                    rows: validRows
                })
            });
            toast.success("Expenses recorded successfully!");
            setRows([EMPTY_ROW()]);
            setPartySearch({});
            if (onClose) onClose();
        } catch (err) {
            toast.error(err.message || "Failed to save expenses");
        } finally {
            setSubmitting(false);
        }
    };

    const handleReset = () => {
        setRows([EMPTY_ROW()]);
        setPartySearch({});
        setSelectedTypeId(expenseTypes.length > 0 ? String(expenseTypes[0].id) : '');
        setSelectedType(expenseTypes[0] || null);
    };

    if (!isOpen && !isPage) return null;

    const th = { padding: '10px 10px', background: '#1a2e44', color: '#fff', fontSize: '13px', fontWeight: '600', textAlign: 'center', whiteSpace: 'nowrap', border: '1px solid #2d4a63' };
    const td = { padding: '6px 6px', border: '1px solid #e8ecf0', background: '#fff', verticalAlign: 'middle' };
    const inp = { width: '100%', padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', background: '#fff', boxSizing: 'border-box' };

    return (
        <div style={{ padding: isPage ? '0' : '0', maxWidth: '100%' }}>
            <h2 style={{ textAlign: 'center', fontSize: '22px', fontWeight: '700', color: '#1a2e44', marginBottom: '20px' }}>Add Expense</h2>

            {/* Expense Type selector */}
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <label style={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Expense Type</label>
                <select
                    value={selectedTypeId}
                    onChange={handleTypeChange}
                    style={{ flex: 1, padding: '9px 12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', background: '#fff' }}
                >
                    <option value="">-- Select Expense Type --</option>
                    {expenseTypes.map(t => (
                        <option key={t.id} value={String(t.id)}>{t.expenseTypeName} ({t.tax1}% + {t.tax2}%)</option>
                    ))}
                </select>
                {expenseTypes.length === 0 && (
                    <span style={{ fontSize: '13px', color: '#e53e3e' }}>No expense types found. Add one in Settings → Expense Types.</span>
                )}
            </div>

            {/* Table */}
            <div style={{ overflow: 'visible', borderRadius: '6px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, width: '200px' }}>Party A/c</th>
                            <th style={{ ...th, width: '120px' }}>Date</th>
                            <th style={{ ...th, width: '140px' }}>Expense Name</th>
                            <th style={{ ...th, width: '110px' }}>Invoice No</th>
                            <th style={{ ...th, width: '140px' }}>Expense Purpose</th>
                            <th style={{ ...th, width: '100px' }}>Net Amount</th>
                            <th style={{ ...th, width: '100px' }}>GST Amount</th>
                            <th style={{ ...th, width: '100px' }}>Row Total</th>
                            <th style={{ ...th, width: '40px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx}>
                                {/* Party A/c with autocomplete */}
                                <td style={td}>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            ref={el => {
                                                if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                                rowRefs.current[idx].partyName = el;
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, idx, 'partyName')}
                                            style={inp}
                                            placeholder="Search party a/c..."
                                            value={partySearch[idx] !== undefined ? partySearch[idx] : row.partyName}
                                            onChange={(e) => handlePartySearch(idx, e.target.value)}
                                            onFocus={() => {
                                                setPartyDropdown(prev => ({ ...prev, [idx]: true }));
                                                setPartyActiveIndex(-1);
                                            }}
                                            onBlur={() => {
                                                setTimeout(() => {
                                                    setPartyDropdown(prev => ({ ...prev, [idx]: false }));
                                                    setPartyActiveIndex(-1);
                                                }, 200);
                                            }}
                                        />
                                        {row.partyType && (
                                            <span style={{ fontSize: '10px', background: row.partyType === 'seller' ? '#e6f4ea' : row.partyType === 'customer' ? '#e8f0fe' : '#fff3e0', color: '#555', padding: '1px 5px', borderRadius: '3px', position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}>
                                                {row.partyType}
                                            </span>
                                        )}
                                        {partyDropdown[idx] && (
                                            <div
                                                ref={el => dropdownRef.current[idx] = el}
                                                style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
                                            >
                                                {getFilteredParties(partySearch[idx] || row.partyName).map((p, pi) => {
                                                    const isHighlighted = partyActiveIndex === pi;
                                                    return (
                                                        <div
                                                            key={pi}
                                                            onMouseDown={() => selectParty(idx, p)}
                                                            onMouseEnter={() => setPartyActiveIndex(pi)}
                                                            onMouseLeave={() => setPartyActiveIndex(-1)}
                                                            style={{
                                                                padding: '8px 12px',
                                                                cursor: 'pointer',
                                                                fontSize: '13px',
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                borderBottom: '1px solid #f0f0f0',
                                                                background: isHighlighted ? '#edf4fc' : '#fff',
                                                                transition: 'background-color 0.1s ease'
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                                                <span style={{ fontWeight: '600', color: '#1a2e44', fontSize: '13px' }}>{p.name}</span>
                                                                <span style={{ fontSize: '11px', color: (p.group && p.group.toLowerCase().includes('expense')) || p.type === 'Account' || p.type === 'account' ? '#333' : (p.balance > 0 ? '#10b981' : p.balance < 0 ? '#ef4444' : '#666'), fontWeight: '500' }}>
                                                                    {(() => {
                                                                        const isExpense = p.group && p.group.toLowerCase().includes('expense');
                                                                        if (isExpense || p.type === 'Account' || p.type === 'account') {
                                                                            return `Expense: ₹${Math.abs(p.balance).toFixed(2)}`;
                                                                        }
                                                                        if (p.balance > 0) {
                                                                            return `Receivable: ₹${p.balance.toFixed(2)}`;
                                                                        }
                                                                        if (p.balance < 0) {
                                                                            return `Payable: ₹${Math.abs(p.balance).toFixed(2)}`;
                                                                        }
                                                                        if (p.type === 'customer' || p.type === 'Customer') {
                                                                            return 'Receivable: ₹0.00';
                                                                        }
                                                                        if (p.type === 'seller' || p.type === 'Seller' || p.type === 'transporter' || p.type === 'Transporter') {
                                                                            return 'Payable: ₹0.00';
                                                                        }
                                                                        return `₹${parseFloat(p.balance).toFixed(2)}`;
                                                                    })()}
                                                                </span>
                                                            </div>
                                                            <span style={{
                                                                fontSize: '11px',
                                                                color: isHighlighted ? '#1a2e44' : '#888',
                                                                textTransform: 'capitalize',
                                                                background: isHighlighted ? '#dbeafe' : '#f5f5f5',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                fontWeight: isHighlighted ? '600' : 'normal',
                                                                transition: 'all 0.1s ease'
                                                            }}>{(p.group && p.group.toLowerCase().includes('expense')) ? p.group : p.type}</span>
                                                        </div>
                                                    );
                                                })}
                                                {getFilteredParties(partySearch[idx] || row.partyName).length === 0 && partySearch[idx] && (
                                                    <div style={{ padding: '10px', color: '#888', fontSize: '13px', textAlign: 'center' }}>No parties found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                {/* Date */}
                                <td style={td}>
                                    <AirDatePicker
                                        ref={el => {
                                            if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                            rowRefs.current[idx].date = el;
                                        }}
                                        value={row.date}
                                        onChange={(dateStr) => updateRow(idx, 'date', dateStr)}
                                        style={inp}
                                    />
                                </td>
                                {/* Expense Name */}
                                <td style={td}>
                                    <input
                                        ref={el => {
                                            if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                            rowRefs.current[idx].expenseName = el;
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e, idx, 'expenseName')}
                                        style={inp}
                                        placeholder="Type name..."
                                        value={row.expenseName}
                                        onChange={(e) => updateRow(idx, 'expenseName', e.target.value)}
                                    />
                                </td>
                                {/* Invoice No */}
                                <td style={td}>
                                    <input
                                        ref={el => {
                                            if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                            rowRefs.current[idx].invoiceNo = el;
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e, idx, 'invoiceNo')}
                                        style={inp}
                                        placeholder="Invoice no..."
                                        value={row.invoiceNo}
                                        onChange={(e) => updateRow(idx, 'invoiceNo', e.target.value)}
                                    />
                                </td>
                                {/* Expense Purpose */}
                                <td style={td}>
                                    <input
                                        ref={el => {
                                            if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                            rowRefs.current[idx].expensePurpose = el;
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e, idx, 'expensePurpose')}
                                        style={inp}
                                        placeholder="Search purpose..."
                                        value={row.expensePurpose}
                                        onChange={(e) => updateRow(idx, 'expensePurpose', e.target.value)}
                                    />
                                </td>
                                {/* Net Amount */}
                                <td style={td}>
                                    <input
                                        ref={el => {
                                            if (!rowRefs.current[idx]) rowRefs.current[idx] = {};
                                            rowRefs.current[idx].netAmount = el;
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e, idx, 'netAmount')}
                                        type="number"
                                        style={{ ...inp, textAlign: 'right' }}
                                        value={row.netAmount}
                                        min="0"
                                        step="0.01"
                                        onChange={(e) => updateRow(idx, 'netAmount', e.target.value)}
                                    />
                                </td>
                                {/* GST Amount (read-only, auto-calculated) */}
                                <td style={{ ...td, background: '#f9f9f9' }}>
                                    <input
                                        type="number"
                                        style={{ ...inp, textAlign: 'right', background: '#f0f4f8', cursor: 'default' }}
                                        value={row.gstAmount}
                                        readOnly
                                    />
                                </td>
                                {/* Row Total (read-only) */}
                                <td style={{ ...td, background: '#f9f9f9' }}>
                                    <input
                                        type="number"
                                        style={{ ...inp, textAlign: 'right', background: '#e8f5e9', fontWeight: '600', cursor: 'default' }}
                                        value={row.rowTotal}
                                        readOnly
                                    />
                                </td>
                                {/* Remove */}
                                <td style={{ ...td, textAlign: 'center' }}>
                                    <button
                                        onClick={() => removeRow(idx)}
                                        style={{ width: '24px', height: '24px', background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', lineHeight: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >×</button>
                                </td>
                            </tr>
                        ))}
                        {/* Totals row */}
                        <tr style={{ background: '#f0f4f8', fontWeight: '700' }}>
                            <td colSpan="5" style={{ ...td, textAlign: 'right', fontSize: '13px', color: '#4a5568' }}>Total</td>
                            <td style={{ ...td, textAlign: 'right', color: '#1a2e44' }}>{totals.net.toFixed(2)}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#1a2e44' }}>{totals.gst.toFixed(2)}</td>
                            <td style={{ ...td, textAlign: 'right', color: '#2d7d46', fontSize: '15px' }}>{totals.total.toFixed(2)}</td>
                            <td style={td}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Add Row button */}
            <div style={{ marginTop: '12px' }}>
                <button onClick={addRow} style={{ padding: '7px 18px', background: '#fff', border: '1px solid #1a2e44', color: '#1a2e44', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                    + Add Row
                </button>
            </div>

            {/* Submit / Reset */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                <button type="button" onClick={onClose} style={{ padding: '10px 28px', background: '#fff', border: '1px solid #ccc', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#555' }}>
                    Cancel
                </button>
                <button onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 32px', background: '#1a2e44', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                    {submitting ? "Saving..." : "Submit"}
                </button>
            </div>
        </div>
    );
}

export default AddExpensesModal;
